<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {BlogPostLayout, Hero} from '@react-spectrum/docs';
export default BlogPostLayout;

---
description: A lot has happened already in the past month but here is another release! FocusScope fixes, TableView updates and general bug fixes make up the bulk of this release. We also have improvements to the newly released `ComboBox`, design updates to `Menu` and `TableView` and a fix to an interesting bug for `Menu` only present on Android Firefox!
date: 2021-08-04
---

# August 4, 2021 Release


A lot has happened already in the past month but here is another release! FocusScope fixes, TableView updates and general bug fixes make up the bulk of this release. We also have improvements to the newly released `ComboBox`, design updates to `Menu` and `TableView` and a fix to an interesting bug for `Menu` only present on Android Firefox!

As always, thank you to all of our amazing contributors for their help with this release 🤩

## New features
- Clear input on cmd + delete in `Numberfield` - [@reidbarber](https://github.com/reidbarber) - [PR](https://github.com/adobe/react-spectrum/pull/2005)
- Add custom document to `watchModals` - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/2045)

## Fixed
- Update TableView to adjust layout when columns change - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2028)
- Pass missing filtered props to `Tabs` - [@reidbarber](https://github.com/reidbarber) - [PR](https://github.com/adobe/react-spectrum/pull/2047)
- Add missing `shouldUseVirtualFocus` to `useListBox` types - [@reidbarber](https://github.com/reidbarber) - [PR](https://github.com/adobe/react-spectrum/pull/2017)
- Prevent autoFocus from being re-executed in `FocusScope` - [@Andarist](https://github.com/Andarist) - [PR](https://github.com/adobe/react-spectrum/pull/1967)
- Update method of merging ids and other props related to slots - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/2073)
- Update `parentKey` when a tree node is moved in `useTreeData` - [@alirezamirian](https://github.com/alirezamirian) - [PR](https://github.com/adobe/react-spectrum/pull/1988)
- Fix ComboBox menu direction on mount - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2069)
- Fix TableView cell content fill 100% width - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2042)
- Add additional check for ref in `computeVisibleItems` - [@onelson](https://github.com/onelson) - [PR](https://github.com/adobe/react-spectrum/pull/2146)
- Modify `isElementInScope` to accommodate for children `FocusScope` - [@Rafal2228](https://github.com/Rafal2228) - [PR](https://github.com/adobe/react-spectrum/pull/2139)
- Fix announcing for sort changes and keyboard navigation in TableView - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2137)
- Change `Field` to allow for correct shrink behavior - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/2176)
- Include `aria-selected` only if selection is present in `useOption` - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2172)
- Update `Menu` to use `word-break: break-word` for text wrapping - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2173)
- Translations and single selection mode labels fixed for TableView - [@majornista](https://github.com/majornista) - [PR](https://github.com/adobe/react-spectrum/pull/2134)
- Fix `MenuTrigger` from closing incorrectly on iOS and Android Firefox - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/2175)
- Update spacing for selection column in TableView - [@ktabors](https://github.com/ktabors) - [PR](https://github.com/adobe/react-spectrum/pull/2145)
- Restore `FocusScope` to the correct node or `body` if outside of scope - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/2166)
- Enable propagation of “all” `selectedKeys` value in `useAsyncList` - [@jluyau](https://github.com/jluyau) - [PR](https://github.com/adobe/react-spectrum/pull/2200)
- Fix peer dependencies in packages - [@jluyau](https://github.com/jluyau) - [PR](https://github.com/adobe/react-spectrum/pull/2201) - [PR](https://github.com/adobe/react-spectrum/pull/2158)

## Docs
- Initialize context in `useCheckboxGroup` and `useRadioGroup` examples - [@reidbarber](https://github.com/reidbarber) - [PR](https://github.com/adobe/react-spectrum/pull/2041)
- Remove unsupported prop in `Breadcrumbs` examples - [@ktabors](https://github.com/ktabors) - [PR](https://github.com/adobe/react-spectrum/pull/1983)
- Update Contribute page to include scaffolding setup - [@DanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/2082)
- Fix incorrect link in Contributing page - [@Zhouzi](https://github.com/Zhouzi) - [PR](https://github.com/adobe/react-spectrum/pull/2135)
- Update to current year in plop setup - [@solimant](https://github.com/solimant) - [PR](https://github.com/adobe/react-spectrum/pull/2148)
## Under construction

Pre-release versions of the following components have been released. Please feel free to try them out, and report any issues you encounter.


- SearchWithin alpha
- Table RC
  - [React Spectrum](https://react-spectrum.adobe.com/react-spectrum/TableView.html)
  - [React Aria](https://react-spectrum.adobe.com/react-aria/useTable.html)
  - [React Stately](https://react-spectrum.adobe.com/react-stately/useTableState.html)



## Released packages
```
- @adobe/react-spectrum => 3.13.0
- @internationalized/message => 3.0.2
- @internationalized/number => 3.0.2
- @react-aria/accordion => 3.0.0-alpha.2
- @react-aria/actiongroup => 3.2.1
- @react-aria/aria-modal-polyfill => 3.4.0
- @react-aria/breadcrumbs => 3.1.5
- @react-aria/button => 3.3.3
- @react-aria/checkbox => 3.2.3
- @react-aria/color => 3.0.0-beta.3
- @react-aria/combobox => 3.0.1
- @react-aria/dialog => 3.1.4
- @react-aria/dnd => 3.0.0-alpha.3
- @react-aria/focus => 3.4.1
- @react-aria/grid => 3.0.0-rc.0
- @react-aria/i18n => 3.3.2
- @react-aria/interactions => 3.5.1
- @react-aria/label => 3.1.3
- @react-aria/link => 3.1.4
- @react-aria/listbox => 3.3.1
- @react-aria/live-announcer => 3.0.1
- @react-aria/menu => 3.2.3
- @react-aria/meter => 3.1.3
- @react-aria/numberfield => 3.0.1
- @react-aria/overlays => 3.7.2
- @react-aria/progress => 3.1.3
- @react-aria/radio => 3.1.5
- @react-aria/searchfield => 3.1.5
- @react-aria/select => 3.4.1
- @react-aria/selection => 3.5.1
- @react-aria/separator => 3.1.3
- @react-aria/slider => 3.0.3
- @react-aria/spinbutton => 3.0.1
- @react-aria/ssr => 3.0.3
- @react-aria/switch => 3.1.3
- @react-aria/table => 3.0.0-rc.0
- @react-aria/tabs => 3.0.1
- @react-aria/textfield => 3.3.1
- @react-aria/toggle => 3.1.4
- @react-aria/tooltip => 3.1.3
- @react-aria/utils => 3.8.2
- @react-aria/virtualizer => 3.3.4
- @react-aria/visually-hidden => 3.2.3
- @react-spectrum/accordion => 3.0.0-alpha.2
- @react-spectrum/actionbar => 3.0.0-alpha.2
- @react-spectrum/actiongroup => 3.2.2
- @react-spectrum/breadcrumbs => 3.2.3
- @react-spectrum/button => 3.5.1
- @react-spectrum/buttongroup => 3.2.2
- @react-spectrum/checkbox => 3.2.4
- @react-spectrum/color => 3.0.0-beta.3
- @react-spectrum/combobox => 3.0.1
- @react-spectrum/dialog => 3.3.3
- @react-spectrum/divider => 3.1.3
- @react-spectrum/form => 3.2.3
- @react-spectrum/icon => 3.3.2
- @react-spectrum/illustratedmessage => 3.1.3
- @react-spectrum/image => 3.1.3
- @react-spectrum/label => 3.3.4
- @react-spectrum/layout => 3.2.1
- @react-spectrum/link => 3.1.3
- @react-spectrum/list => 3.0.0-alpha.2
- @react-spectrum/listbox => 3.5.1
- @react-spectrum/menu => 3.4.0
- @react-spectrum/meter => 3.1.3
- @react-spectrum/numberfield => 3.0.1
- @react-spectrum/overlays => 3.4.4
- @react-spectrum/picker => 3.3.1
- @react-spectrum/progress => 3.1.3
- @react-spectrum/provider => 3.2.2
- @react-spectrum/radio => 3.1.4
- @react-spectrum/searchfield => 3.1.5
- @react-spectrum/searchwithin => 3.0.0-alpha.0
- @react-spectrum/slider => 3.0.4
- @react-spectrum/statuslight => 3.2.2
- @react-spectrum/switch => 3.1.3
- @react-spectrum/table => 3.0.0-rc.0
- @react-spectrum/tabs => 3.0.2
- @react-spectrum/text => 3.1.3
- @react-spectrum/textfield => 3.1.7
- @react-spectrum/theme-dark => 3.2.2
- @react-spectrum/theme-default => 3.2.2
- @react-spectrum/theme-light => 3.1.2
- @react-spectrum/tooltip => 3.1.4
- @react-spectrum/utils => 3.6.2
- @react-spectrum/view => 3.1.3
- @react-spectrum/well => 3.1.3
- @react-stately/checkbox => 3.0.3
- @react-stately/collections => 3.3.3
- @react-stately/color => 3.0.0-beta.3
- @react-stately/combobox => 3.0.1
- @react-stately/data => 3.4.1
- @react-stately/dnd => 3.0.0-alpha.2
- @react-stately/grid => 3.0.0-rc.0
- @react-stately/layout => 3.4.0
- @react-stately/list => 3.3.0
- @react-stately/menu => 3.2.3
- @react-stately/numberfield => 3.0.2
- @react-stately/overlays => 3.1.3
- @react-stately/radio => 3.3.2
- @react-stately/searchfield => 3.1.3
- @react-stately/select => 3.1.3
- @react-stately/selection => 3.7.0
- @react-stately/slider => 3.0.3
- @react-stately/table => 3.0.0-rc.0
- @react-stately/tabs => 3.0.1
- @react-stately/toggle => 3.2.3
- @react-stately/tooltip => 3.0.5
- @react-stately/tree => 3.2.0
- @react-stately/utils => 3.2.2
- @react-stately/virtualizer => 3.1.5
- @react-types/accordion => 3.0.0-alpha.1
- @react-types/actionbar => 3.0.0-alpha.1
- @react-types/actiongroup => 3.2.1
- @react-types/breadcrumbs => 3.2.1
- @react-types/button => 3.4.1
- @react-types/buttongroup => 3.1.2
- @react-types/checkbox => 3.2.3
- @react-types/color => 3.0.0-beta.2
- @react-types/combobox => 3.0.1
- @react-types/dialog => 3.3.1
- @react-types/divider => 3.1.2
- @react-types/form => 3.2.2
- @react-types/grid => 3.0.0-rc.0
- @react-types/illustratedmessage => 3.1.2
- @react-types/image => 3.1.2
- @react-types/label => 3.4.1
- @react-types/layout => 3.1.2
- @react-types/link => 3.1.3
- @react-types/listbox => 3.2.1
- @react-types/menu => 3.3.0
- @react-types/meter => 3.1.2
- @react-types/numberfield => 3.0.1
- @react-types/overlays => 3.5.1
- @react-types/progress => 3.1.2
- @react-types/provider => 3.3.2
- @react-types/radio => 3.1.2
- @react-types/searchfield => 3.1.2
- @react-types/searchwithin => 3.0.0-alpha.0
- @react-types/select => 3.3.1
- @react-types/shared => 3.8.0
- @react-types/slider => 3.0.2
- @react-types/statuslight => 3.1.2
- @react-types/switch => 3.1.2
- @react-types/tabs => 3.0.1
- @react-types/text => 3.1.3
- @react-types/textfield => 3.2.3
- @react-types/tooltip => 3.1.2
- @react-types/view => 3.1.2
- @react-types/well => 3.1.2
- @spectrum-icons/color => 3.3.1
- @spectrum-icons/illustrations => 3.2.2
- @spectrum-icons/ui => 3.2.1
- @spectrum-icons/workflow => 3.2.1
- react-aria => 3.9.0
- react-stately => 3.8.0
```
